import { ref, uploadBytes, getDownloadURL, StorageError } from "firebase/storage";
import { storage } from "../../../Firebase";

// 画像の最大サイズ（10MB）
const MAX_FILE_SIZE = 10 * 1024 * 1024;

// 許可される画像形式
const ALLOWED_IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg', 
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml'
];

// ファイル名をサニタイズする関数
const sanitizeFileName = (fileName: string): string => {
  // 拡張子を保持
  const lastDotIndex = fileName.lastIndexOf('.');
  const nameWithoutExt = lastDotIndex > -1 ? fileName.substring(0, lastDotIndex) : fileName;
  const extension = lastDotIndex > -1 ? fileName.substring(lastDotIndex) : '';
  
  // ファイル名から特殊文字を除去（日本語も除去）
  const sanitizedName = nameWithoutExt
    .replace(/[^\w\s-]/g, '') // 英数字、スペース、ハイフン以外を除去
    .replace(/\s+/g, '_')     // スペースをアンダースコアに
    .replace(/-+/g, '-')      // 連続するハイフンを単一に
    .replace(/^-+|-+$/g, '')  // 先頭末尾のハイフンを除去
    .substring(0, 50);        // 長さ制限
  
  // サニタイズ後に空になった場合はタイムスタンプを使用
  const finalName = sanitizedName || `image_${Date.now()}`;
  
  return finalName + extension;
};

// 画像のバリデーション
const validateImage = (file: File): void => {
  // ファイルサイズチェック
  if (file.size > MAX_FILE_SIZE) {
    throw new Error(`ファイルサイズが大きすぎます。最大${MAX_FILE_SIZE / 1024 / 1024}MBまでです。`);
  }
  
  // ファイル形式チェック
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    throw new Error(`サポートされていないファイル形式です。対応形式: JPEG, PNG, GIF, WebP, SVG`);
  }
};

export const uploadImage = async (
  file: File,
  projectId: string,
  eventId: string,
  problemId: string,
  imageType: "problem" | "answer" | "before" = "problem"
): Promise<string> => {
  try {
    // バリデーション
    validateImage(file);
    
    const timestamp = Date.now();
    const sanitizedFileName = sanitizeFileName(file.name);
    const fileName = `${imageType}_${timestamp}_${sanitizedFileName}`;
    const path = `v3/projects/${projectId}/events/${eventId}/problems/${problemId}/${fileName}`;
    
    console.log('Uploading image:', {
      path,
      size: `${(file.size / 1024).toFixed(2)}KB`,
      type: file.type,
      originalName: file.name,
      sanitizedName: fileName
    });
    
    const storageRef = ref(storage, path);
    
    // メタデータの設定
    const metadata = {
      contentType: file.type,
      cacheControl: 'public, max-age=3600', // 1時間のキャッシュ
      customMetadata: {
        uploadedAt: new Date().toISOString(),
        originalName: file.name,
        projectId,
        eventId,
        problemId,
        imageType
      }
    };
    
    // アップロード実行
    const snapshot = await uploadBytes(storageRef, file, metadata);
    
    // ダウンロードURLを取得
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    console.log('Upload successful:', downloadURL);
    return downloadURL;
    
  } catch (error) {
    console.error('Upload error:', error);
    
    // エラーメッセージの改善
    if (error instanceof StorageError) {
      switch (error.code) {
        case 'storage/unauthorized':
          throw new Error('画像のアップロード権限がありません。ログインしてください。');
        case 'storage/canceled':
          throw new Error('アップロードがキャンセルされました。');
        case 'storage/unknown':
          throw new Error('不明なエラーが発生しました。もう一度お試しください。');
        default:
          throw new Error(`アップロードエラー: ${error.message}`);
      }
    } else if (error instanceof Error) {
      throw error;
    } else {
      throw new Error('予期しないエラーが発生しました。');
    }
  }
};

export const uploadMultipleImages = async (
  files: File[],
  projectId: string,
  eventId: string,
  problemId: string,
  imageType: "problem" | "answer" | "before" = "problem"
): Promise<string[]> => {
  // 並列アップロードの制限（同時に3つまで）
  const CONCURRENT_UPLOADS = 3;
  const results: string[] = [];
  
  for (let i = 0; i < files.length; i += CONCURRENT_UPLOADS) {
    const batch = files.slice(i, i + CONCURRENT_UPLOADS);
    const batchPromises = batch.map(file => 
      uploadImage(file, projectId, eventId, problemId, imageType)
    );
    
    const batchResults = await Promise.all(batchPromises);
    results.push(...batchResults);
  }
  
  return results;
};